import React, { useState, useEffect, useRef } from 'react';
import { Message } from '../../types/MessageTypes';
import chatService from '../../services/chat.service';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';

interface ChatPanelProps {
  referralId: string;
  vacancyTitle: string;
  candidateName: string;
  isOpen: boolean;
  onClose: () => void;
  darkMode?: boolean;
}

const ChatPanel: React.FC<ChatPanelProps> = ({
  referralId,
  vacancyTitle,
  candidateName,
  isOpen,
  onClose,
  darkMode,
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      chatService.getMessages(referralId).then(setMessages);
      chatService.markAsRead(referralId);
    }
  }, [isOpen, referralId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim() || sending) return;
    setSending(true);
    try {
      const msg = await chatService.sendMessage(referralId, input.trim());
      setMessages(prev => [...prev, msg]);
      setInput('');
    } catch {
      // Send failed silently
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`chat-panel ${darkMode ? 'dark' : ''}`}>
      <div className="chat-panel-header">
        <div>
          <h3>{candidateName}</h3>
          <p className="chat-panel-subtitle">{vacancyTitle}</p>
        </div>
        <button className="chat-panel-close" onClick={onClose} aria-label="Close chat">
          <CloseIcon sx={{ fontSize: 20 }} />
        </button>
      </div>

      <div className="chat-messages">
        {messages.length === 0 ? (
          <div className="chat-empty">
            <p>No messages yet. Start the conversation!</p>
          </div>
        ) : (
          messages.map(msg => (
            <div
              key={msg.id}
              className={`chat-message ${msg.sender_id === 'mock-user' ? 'sent' : 'received'}`}
            >
              <div className="chat-message-content">{msg.content}</div>
              <div className="chat-message-meta">
                <span className="chat-message-sender">{msg.sender_name}</span>
                <span className="chat-message-time">
                  {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="chat-input-bar">
        <input
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message..."
          disabled={sending}
        />
        <button className="chat-send-btn" onClick={handleSend} disabled={!input.trim() || sending}>
          <SendIcon sx={{ fontSize: 20 }} />
        </button>
      </div>
    </div>
  );
};

export default ChatPanel;
